import type { Playbook, PlaybookReadiness, PlaybookStatus } from '@doneverse/shared'
import { api, readNotReadyPayload } from '../api/index.js'

// Uploads and change history are not playbook-specific — they live in
// `cmsAssetsService`, which every CMS surface imports directly.

/**
 * Admin authoring API for playbooks.
 *
 * Separate from `adminService`'s handful of playbook calls because this is the
 * CMS surface — create/patch every authored field, media uploads, additional
 * videos, publish, archive, history. The asset and history endpoints are shared
 * with the other content surfaces (Masterclass, Lounge) rather than being
 * playbook-specific, hence the generic `scope` / `table` parameters.
 */

/** Any authored field. Mirrors the API's parsePlaybookFields allowlist. */
export interface PlaybookCmsPatch {
  name?: string
  slug?: string
  status?: PlaybookStatus
  description?: string | null
  type?: Playbook['type']
  category?: Playbook['category']
  blueprint_step_id?: string | null
  doing_team_id?: string | null
  headline?: string | null
  founder_page_subheadline?: string | null
  founder_page_summary?: string | null
  founder_page_steps?: { title: string; description: string }[] | null
  doer_page_subheadline?: string | null
  doer_page_summary?: string | null
  doer_page_steps?: { title: string; description: string }[] | null
  video_r2_key?: string | null
  thumbnail_r2_key?: string | null
  video_thumbnail_r2_key?: string | null
}

export interface PlaybookAdditionalVideoAdmin {
  id: string
  playbook_id: string
  position: number
  title: string
  description: string | null
  /** Raw key — round-tripped on save, never replaced by a signed URL. */
  r2_key: string
  thumbnail_r2_key: string | null
  video_preview_url: string | null
  thumbnail_preview_url: string | null
  created_at: string
  updated_at: string
}

/**
 * Readiness plus the founder categories that recommend this playbook — the API
 * returns them together because the same count drives the readiness warning.
 */
export interface PlaybookReadinessResponse extends PlaybookReadiness {
  recommended_categories: string[]
}

/** Thrown when the API refuses a publish; carries what is still missing. */
export class PlaybookNotReadyError extends Error {
  constructor(readonly readiness: Pick<PlaybookReadiness, 'blocking' | 'warnings'>) {
    super('Playbook is not ready to publish')
    this.name = 'PlaybookNotReadyError'
  }
}

export const playbookCmsService = {
  list: async (options: { status?: PlaybookStatus } = {}): Promise<Playbook[]> => {
    const query = options.status ? `&status=${options.status}` : ''
    return api.get(`/playbooks?all=true${query}`).json<Playbook[]>()
  },

  get: async (identifier: string): Promise<Playbook> => {
    return api.get(`/playbooks/${identifier}`).json<Playbook>()
  },

  create: async (input: PlaybookCmsPatch & { name: string }): Promise<Playbook> => {
    return api.post(input, '/playbooks').json<Playbook>()
  },

  update: async (id: string, patch: PlaybookCmsPatch): Promise<Playbook> => {
    try {
      return await api.patch(patch, `/playbooks/${id}`).json<Playbook>()
    } catch (error) {
      const readiness = readNotReadyPayload(error)
      if (readiness) throw new PlaybookNotReadyError(readiness)
      throw error
    }
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/playbooks/${id}`).res()
  },

  readiness: async (id: string): Promise<PlaybookReadinessResponse> => {
    return api.get(`/v1/admin/playbooks/${id}/readiness`).json<PlaybookReadinessResponse>()
  },

  // ── Additional videos ───────────────────────────────────────────────────

  listVideos: async (playbookId: string): Promise<PlaybookAdditionalVideoAdmin[]> => {
    return api.get(`/v1/admin/playbooks/${playbookId}/videos`).json<PlaybookAdditionalVideoAdmin[]>()
  },

  addVideo: async (
    playbookId: string,
    input: { title: string; description?: string | null; r2_key: string; thumbnail_r2_key?: string | null },
  ): Promise<PlaybookAdditionalVideoAdmin> => {
    return api.post(input, `/v1/admin/playbooks/${playbookId}/videos`).json<PlaybookAdditionalVideoAdmin>()
  },

  updateVideo: async (
    playbookId: string,
    videoId: string,
    patch: { title?: string; description?: string | null; thumbnail_r2_key?: string | null },
  ): Promise<PlaybookAdditionalVideoAdmin> => {
    return api
      .patch(patch, `/v1/admin/playbooks/${playbookId}/videos/${videoId}`)
      .json<PlaybookAdditionalVideoAdmin>()
  },

  removeVideo: async (playbookId: string, videoId: string): Promise<void> => {
    await api.delete(`/v1/admin/playbooks/${playbookId}/videos/${videoId}`).res()
  },

  reorderVideos: async (playbookId: string, videoIds: string[]): Promise<PlaybookAdditionalVideoAdmin[]> => {
    return api
      .put({ video_ids: videoIds }, `/v1/admin/playbooks/${playbookId}/videos/reorder`)
      .json<PlaybookAdditionalVideoAdmin[]>()
  },
}
